import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Bell } from 'lucide-react';
import { usePushNotifications } from '@/hooks/usePushNotifications';

const SCAN_COUNT_KEY = 'scout_scanCount';
const DISMISS_KEY = 'scout_pushDismissedAt';
const TRIGGER_SCAN_COUNT = 2;
const DISMISS_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

interface PushOptInPromptProps {
  onClose: () => void;
}

export function incrementScanCount(): number {
  const count = parseInt(localStorage.getItem(SCAN_COUNT_KEY) || '0', 10) + 1;
  localStorage.setItem(SCAN_COUNT_KEY, String(count));
  return count;
}

export function shouldShowPushPrompt(): boolean {
  if (!('PushManager' in window)) return false;

  // Only show after the user has completed at least one successful scan
  if (!localStorage.getItem('scout_first_scan_done')) return false;

  const count = parseInt(localStorage.getItem(SCAN_COUNT_KEY) || '0', 10);
  if (count < TRIGGER_SCAN_COUNT) return false;

  const dismissedAt = localStorage.getItem(DISMISS_KEY);
  if (dismissedAt) {
    const elapsed = Date.now() - parseInt(dismissedAt, 10);
    if (elapsed < DISMISS_COOLDOWN_MS) return false;
  }

  return Notification.permission === 'default';
}

export default function PushOptInPrompt({ onClose }: PushOptInPromptProps) {
  const { t } = useTranslation('scout');
  const { subscribe } = usePushNotifications();
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    // Check if already granted/denied
    if (Notification.permission !== 'default') {
      setVisible(false);
      onClose();
    }
  }, [onClose]);

  const handleEnable = async () => {
    await subscribe();
    setVisible(false);
    onClose();
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
    onClose();
  };

  return (
    <AnimatePresence>
      {visible && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/40 z-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleDismiss}
          />

          {/* Bottom sheet */}
          <motion.div
            className="fixed bottom-0 inset-x-0 z-50 bg-white rounded-t-2xl shadow-xl pb-safe"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          >
            {/* Drag handle */}
            <div className="flex justify-center pt-3 pb-2">
              <div className="w-10 h-1 rounded-full bg-muted-foreground/20" />
            </div>

            <div className="px-6 pb-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Bell className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="text-section font-bold text-foreground">{t('push.optInTitle')}</h3>
                  <p className="text-small text-muted-foreground">{t('push.optInBody')}</p>
                </div>
              </div>

              <div className="flex flex-col gap-2 mt-4">
                <button
                  onClick={handleEnable}
                  className="w-full py-3.5 rounded-xl bg-gradient-to-r from-primary to-forest text-white font-medium text-small active:scale-[0.98] transition-all"
                >
                  {t('push.enable')}
                </button>
                <button
                  onClick={handleDismiss}
                  className="w-full py-3 rounded-xl text-muted-foreground font-medium text-small"
                >
                  {t('push.notNow')}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
